import React, { Fragment, useState, useEffect, useMemo, useRef } from "react";
import { getFieldPath, onCommandEnter } from "utils/form";
import { getFieldDeleteItemName } from "utils/builder";
import { FieldArray, useFormikContext } from "formik";
import {
  INPUT_CLASSES,
  LIST_PICKER_CUSTOM_CLASSES,
  UI_THEME
} from "utils/constants/ui";
import UncontrolledInput from "components/UncontrolledInput";
import OptionSetsSelect from "components/OptionSetsSelect";
import Select from "components/Form/fields/Select";
import {
  DEFAULT_DEBOUNCE,
  FIELD_TEMPLATES,
  UUID_VALUE_KEY
} from "utils/constants/form";
import { DragDropContext, Droppable } from "react-beautiful-dnd";
import {
  listDragProps,
  LIST_DESTINATIONS,
  reorderModelsCollection
} from "utils/drag";
import DraggableOptionItem from "./DraggableOptionItem";
import { usePageBuilderContext } from "utils/context";
import { DRAG_SELECT_OPTION } from "utils/analytics";
import {
  useFormCollections,
  useIsManifestEditMode
} from "utils/hooks/manifest";
import get from "lodash/get";
import { reduceFieldSelectionRequirements } from "utils/combination";
import Button from "components/Form/fields/Button";
import { STATE_KEYS } from "utils/constants/state";
import { useSelectOptions } from "utils/hooks/form";
import FieldListFactory from "components/FieldListFactory";
import { MULTISELECT_PRESENTATION_TYPE } from "utils/constants/multiselect";
import OptionListPicker from "components/OptionListPicker";
import { useAppCheckout } from "utils/selectors";

const AddMultiselectOptionInput = ({ arrayHelpers }) => {
  const ref = useRef(null);
  const [disabled, setDisabled] = useState(true);
  const addOption = () => {
    const label = ref && ref.current && ref.current.value;
    if (label) {
      const newOption = FIELD_TEMPLATES.SELECT.OPTION({
        label
      });
      arrayHelpers.unshift(newOption);
      ref.current.value = "";
      setTimeout(() => setDisabled(true), 0);
    }
  };

  const inputProps = {
    placeholder: "New option",
    debounce: DEFAULT_DEBOUNCE,
    minRows: 1,
    innerRef: ref,
    valueSet: (val) => {
      if (disabled && val) {
        setDisabled(false);
      } else if (!disabled && !val) {
        setDisabled(true);
      }
    },
    onKeyDown: onCommandEnter(addOption)
  };

  return (
    <div className="flex flex-column items-end">
      <UncontrolledInput {...inputProps} />
      <div className="dib pt2">
        <Button
          disabled={disabled}
          copy="Add"
          theme={UI_THEME.SLIM}
          onClick={addOption}
        />
      </div>
    </div>
  );
};

const ValuesFields = ({ name, noBlocksStyle }) => {
  const {
    values,
    touched,
    setFieldValue,
    setFieldTouched
  } = useFormikContext();
  const pageContext = usePageBuilderContext();
  const isEditMode = useIsManifestEditMode();
  const tracking = pageContext ? pageContext.tracking : {};

  const { combinations, fieldGroups, selectedFields } = useFormCollections();

  const uuidPath = getFieldPath(name, "uuid");
  const uuidValue = get(values, uuidPath, []);
  const checkout = useAppCheckout();

  const valueResultsMap = useMemo(
    () => reduceFieldSelectionRequirements({ combinations, uuid: uuidValue }),
    [combinations.length, uuidValue]
  );

  const optionsPath = getFieldPath(name, "options");
  const optionsValue = get(values, optionsPath, []);

  const defaultPath = getFieldPath(name, "default");
  const defaultValue = get(values, defaultPath);
  const defaultTouched = get(touched, defaultPath);

  const item = getFieldDeleteItemName(optionsPath);

  const defaultOption = FIELD_TEMPLATES.SELECT.OPTION();
  const allSelectOptions = useSelectOptions({
    template: defaultOption,
    name,
    options: optionsValue
  });
  const selectOptionsCount = allSelectOptions.length;
  const hasOptions = Boolean(selectOptionsCount > 1);
  const optionsSetsPath = getFieldPath(
    name,
    STATE_KEYS.MULTISELECT.OPTIONS_SETS
  );
  const optionsSets = get(values, optionsSetsPath);

  const optionsSetsCount = optionsSets ? Object.keys(optionsSets).length : 0;

  const presentationTypePath = getFieldPath(
    name,
    STATE_KEYS.MULTISELECT.PRESENTATION_TYPE
  );
  const presentationTypeValue = get(values, presentationTypePath);
  const presentationTypeTouched = get(touched, presentationTypePath);
  const presentationTypeOptions =
    get(checkout, STATE_KEYS.APP_CHECKOUT.MULTISELECT_TYPES) || [];

  /**
   * Unset default if:
   * - options are not present
   * - default value is present but its uuid is not in options
   */
  useEffect(() => {
    if (Array.isArray(defaultValue) && defaultValue.length) {
      if (!hasOptions) {
        setFieldValue(defaultPath, []);
      } else {
        let unset = false;
        for (let valIx = 0; valIx < defaultValue.length; valIx++) {
          const val = defaultValue[valIx];
          unset = !allSelectOptions.find(({ uuid }) => uuid === val);
          if (unset) {
            break;
          }
        }
        if (unset) {
          setFieldValue(defaultPath, []);
        }
      }
    }
  }, [hasOptions, defaultValue, optionsSets]);

  /**
   * If
   * - transitioning to option sets and
   * - component is not select
   * Then
   * - set it to be select
   * Why?
   * - because option sets have a lot of options and will overflow list presentation
   */
  useEffect(() => {
    const shouldUpdate =
      optionsSetsCount > 0 &&
      presentationTypeValue !== MULTISELECT_PRESENTATION_TYPE.SELECT;

    if (shouldUpdate) {
      setTimeout(() => {
        setFieldValue(
          presentationTypePath,
          MULTISELECT_PRESENTATION_TYPE.SELECT
        );
        if (!presentationTypeTouched) {
          setFieldTouched(presentationTypePath, true);
        }
      }, 0);
    }
  }, [optionsSetsCount]);

  const defaultSelectProps = {
    options: allSelectOptions,
    stateKey: defaultPath,
    value: defaultValue,
    valueKey: UUID_VALUE_KEY,
    disabled: !hasOptions || isEditMode,
    onChange: () => (evt) => {
      const selection = evt.currentTarget.value;

      const selectedValue =
        !selection || defaultOption.uuid === selection ? [] : [selection];

      setFieldValue(defaultPath, selectedValue);
      if (!defaultTouched) {
        setFieldTouched(defaultPath, true);
      }
    },
    labelTransform: (val) => val,
    customClasses: {
      container: "w-100",
      input: INPUT_CLASSES
    }
  };
  const defaultOptionSelect = (
    <div className="w-100 pt3">
      <label className="f6 mb2 dib fw6">Default</label>
      <Select {...defaultSelectProps} />
    </div>
  );

  const presentationTypeProps = {
    label: "Presentation type",
    value: presentationTypeValue,
    name: presentationTypePath,
    multiselect: false,
    disabled: isEditMode,
    options: presentationTypeOptions,
    onClick: (_, selectedValue) => {
      setFieldValue(presentationTypePath, selectedValue);
      if (!get(touched, presentationTypePath)) {
        setFieldTouched(name, true);
      }
    },
    canUnset: false,
    customClasses: LIST_PICKER_CUSTOM_CLASSES
  };

  return (
    <Fragment>
      <FieldListFactory
        name={name}
        config={[
          {
            name: "label"
          },
          {
            name: "description"
          },
          {
            name: "placeholder"
          }
        ]}
      />
      {defaultOptionSelect}
      <FieldArray
        name={optionsPath}
        render={(arrayHelpers) => {
          const optionNodes = optionsValue.map((model, modelIx) => {
            const modelName = `${optionsPath}[${modelIx}]`;

            const modelProps = {
              name: modelName,
              model,
              modelIx: modelIx,
              /**
               * Use uuid for two purposes
               * - Drag and drop API
               * - Pass through to option component
               */
              uid: model.uuid,
              uuid: model.uuid,
              index: modelIx,
              arrayHelpers: arrayHelpers,
              item,
              dragDisabled: isEditMode,
              disabled: isEditMode,
              fieldName: name,
              valueResults: valueResultsMap[model.uuid],
              combinations
            };

            return (
              <DraggableOptionItem
                key={`${model.uuid}-${modelIx}`}
                {...modelProps}
              />
            );
          });

          const addOptionProps = {
            arrayHelpers,
            setFieldValue,
            setFieldTouched,
            touched,
            name,
            fieldGroups,
            selectedFields,
            combinations,
            item
          };

          return (
            <Fragment>
              <div className="flex flex-column pt3 mt4 bt hairline-1">
                <div className="f5 mb2 dib fw6">Options</div>
                <div className="pt2 pb3 flex flex-column">
                  <div className="f6 mb2 dib fw5">Custom</div>
                  <div className="f6 mb2 dib ">Create your own options.</div>
                  <AddMultiselectOptionInput {...addOptionProps} />
                </div>
              </div>
              {optionNodes.length > 0 ? (
                <DragDropContext
                  onDragEnd={reorderModelsCollection({
                    setFieldValue,
                    setFieldTouched,
                    models: optionsValue,
                    modelsPath: optionsPath,
                    tracking: {
                      event: DRAG_SELECT_OPTION,
                      data: {
                        ...tracking
                      }
                    }
                  })}
                >
                  <Droppable droppableId={LIST_DESTINATIONS.OPTIONS}>
                    {(provided, snapshot) => (
                      <ul
                        {...listDragProps({
                          provided,
                          snapshot,
                          classes: "ma0 pa0 list w-100"
                        })}
                      >
                        {optionNodes}
                        {provided.placeholder}
                      </ul>
                    )}
                  </Droppable>
                </DragDropContext>
              ) : null}
              <div className="pb3 flex flex-column">
                <div className="f6 mb2 dib fw5">Sets</div>
                <div className="f6 mb2 dib ">
                  Use a set of existing options.
                </div>
                <OptionSetsSelect name={optionsSetsPath} />
              </div>
            </Fragment>
          );
        }}
      />
      <div className="w-100 pt3 mt4 bt hairline-1">
        <div className="f5 pb2 fw6">Presentation</div>
        <div className="f6 lh-copy">Type of select component to show.</div>
        <div className="pt3">
          <OptionListPicker {...presentationTypeProps} />
        </div>
      </div>
    </Fragment>
  );
};

export default ValuesFields;
